import React, { useState } from 'react';
import { 
  Container, 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Paper, 
  Alert,
  Snackbar,
  Divider,
  useTheme,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  InputAdornment
} from '@mui/material';
import { 
  AdminPanelSettings as AdminIcon,
  PersonAdd as PersonAddIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material';
import { promoteToAdmin, createUser } from '../utils/adminUtils';
import { useAuth } from '../context/AuthContext';
import { Navigate } from 'react-router-dom';

const AdminTools = () => {
  const [email, setEmail] = useState('');
  const [newUserData, setNewUserData] = useState({
    email: '',
    password: '',
    username: '',
    role: 'viewer'
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [createUserLoading, setCreateUserLoading] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });
  const { user, getUserRole } = useAuth();
  const [userRole, setUserRole] = useState(null);
  const [checkingRole, setCheckingRole] = useState(true);
  const theme = useTheme();

  React.useEffect(() => {
    const checkUserRole = async () => {
      if (user) {
        try {
          const role = await getUserRole();
          setUserRole(role);
        } catch (error) {
          console.error('Error getting user role:', error);
        } finally {
          setCheckingRole(false);
        }
      } else {
        setCheckingRole(false);
      }
    };

    checkUserRole();
  }, [user, getUserRole]);

  const handlePromoteUser = async (e) => {
    e.preventDefault();
    
    if (!email) {
      setNotification({
        open: true,
        message: 'Por favor ingresa un email',
        severity: 'error'
      });
      return;
    }
    
    setLoading(true);
    
    try {
      const result = await promoteToAdmin(email);
      
      setNotification({
        open: true,
        message: result.message,
        severity: result.success ? 'success' : 'error'
      });
      
      if (result.success) {
        setEmail('');
      }
    } catch (error) {
      setNotification({
        open: true,
        message: `Error: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };
  
  const handleCreateUser = async (e) => {
    e.preventDefault();
    
    // Validación básica
    if (!newUserData.email || !newUserData.password) {
      setNotification({
        open: true,
        message: 'El email y la contraseña son obligatorios',
        severity: 'error'
      });
      return;
    }
    
    setCreateUserLoading(true);
    
    try {
      const result = await createUser(
        newUserData.email,
        newUserData.password,
        newUserData.role,
        newUserData.username
      );
      
      setNotification({
        open: true,
        message: result.message,
        severity: result.success ? 'success' : 'error'
      });
      
      if (result.success) {
        // Limpiar el formulario
        setNewUserData({
          email: '',
          password: '',
          username: '',
          role: 'viewer'
        });
      }
    } catch (error) {
      setNotification({
        open: true,
        message: `Error: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setCreateUserLoading(false);
    }
  };
  
  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  const handleNewUserChange = (e) => {
    const { name, value } = e.target;
    setNewUserData({
      ...newUserData,
      [name]: value
    });
  };

  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  // Si está cargando, muestra un indicador de carga
  if (checkingRole) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Typography>Verificando permisos...</Typography>
      </Box>
    );
  }

  // Si no es admin, redirige a la página principal
  if (!user || userRole !== 'admin') {
    return <Navigate to="/" />;
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper 
        elevation={3} 
        sx={{ 
          p: 4, 
          borderRadius: 2,
          background: `linear-gradient(135deg, 
            ${theme.palette.background.paper} 0%, 
            ${theme.palette.background.default} 100%)`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <AdminIcon fontSize="large" color="primary" sx={{ mr: 2 }} />
          <Typography variant="h4" component="h1">
            Herramientas de Administración
          </Typography>
        </Box>
        
        <Divider sx={{ mb: 4 }} />
        
        <Grid container spacing={4}>
          {/* Sección de Creación de Usuario */}
          <Grid item xs={12} md={6}>
            <Card 
              elevation={2} 
              sx={{ 
                height: '100%',
                background: `linear-gradient(135deg, 
                  ${theme.palette.background.paper} 0%, 
                  ${theme.palette.primary.dark}10 100%)`,
              }}
            >
              <CardHeader 
                title="Crear Usuario" 
                avatar={<PersonAddIcon color="primary" />}
              />
              <CardContent>
                <Box component="form" onSubmit={handleCreateUser}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        label="Email"
                        variant="outlined"
                        fullWidth
                        required
                        name="email"
                        value={newUserData.email}
                        onChange={handleNewUserChange}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label="Contraseña"
                        variant="outlined"
                        fullWidth
                        required
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        value={newUserData.password}
                        onChange={handleNewUserChange}
                        size="small"
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={toggleShowPassword}
                                edge="end"
                              >
                                {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                              </IconButton>
                            </InputAdornment>
                          )
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label="Nombre de Usuario (opcional)"
                        variant="outlined"
                        fullWidth
                        name="username"
                        value={newUserData.username}
                        onChange={handleNewUserChange}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Rol</InputLabel>
                        <Select
                          name="role"
                          value={newUserData.role}
                          label="Rol"
                          onChange={handleNewUserChange}
                        >
                          <MenuItem value="viewer">Viewer</MenuItem>
                          <MenuItem value="admin">Admin</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <Button 
                        type="submit" 
                        variant="contained" 
                        color="primary"
                        fullWidth
                        disabled={createUserLoading}
                      >
                        {createUserLoading ? 'Creando...' : 'Crear Usuario'}
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          {/* Sección de Promoción a Admin */}
          <Grid item xs={12} md={6}>
            <Card 
              elevation={2} 
              sx={{ 
                height: '100%',
                background: `linear-gradient(135deg, 
                  ${theme.palette.background.paper} 0%, 
                  ${theme.palette.primary.dark}10 100%)`,
              }}
            >
              <CardHeader 
                title="Promover a Administrador" 
                avatar={<AdminIcon color="primary" />}
              />
              <CardContent>
                <Box component="form" onSubmit={handlePromoteUser}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        label="Email del usuario"
                        variant="outlined"
                        fullWidth
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="ejemplo@correo.com"
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button 
                        type="submit" 
                        variant="contained" 
                        color="primary"
                        fullWidth
                        disabled={loading}
                      >
                        {loading ? 'Procesando...' : 'Promover a Admin'}
                      </Button>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">
                        Esta acción otorgará permisos de administrador al usuario especificado.
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>
      
      <Snackbar 
        open={notification.open} 
        autoHideDuration={6000} 
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity={notification.severity} 
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AdminTools;
